import React from 'react';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyService';

/**
 * Currency Display Component
 * Handles single currency display and dual currency display for USD-only payment methods
 */
const CurrencyDisplay = ({ 
  currencyData, 
  className = '', 
  showLabel = true,
  labelText = 'Total',
  size = 'normal', // 'small', 'normal', 'large'
  t = null // Translation object
}) => {
  if (!currencyData) {
    return null;
  }

  const {
    showDualCurrency,
    localAmount,
    localCurrency,
    usdAmount,
    paymentCurrency,
    paymentAmount
  } = currencyData;

  // Size classes
  const sizeClasses = {
    small: 'text-sm',
    normal: 'text-base',
    large: 'text-lg font-bold'
  };

  const sizeClass = sizeClasses[size] || sizeClasses.normal;

  if (showDualCurrency) {
    // Dual currency display for USD-only payment methods
    return (
      <div className={`currency-display dual-currency ${className}`}>
        {showLabel && (
          <span className="currency-label text-gray-600 mr-2">
            {labelText}:
          </span>
        )}
        
        <div className="currency-amounts">
          {/* Local currency amount (larger, primary) */}
          <div className={`local-currency ${sizeClass} text-gray-900`}>
            {formatCurrency(localAmount, localCurrency)}
          </div>
          
          {/* USD amount (smaller, secondary) */}
          <div className="usd-currency text-sm text-gray-500 mt-1">
            ({formatCurrency(usdAmount, 'USD')} USD - {t ? t.usdChargeNote : "You'll be charged in USD"})
          </div>
          
          {/* Payment method note */}
          <div className="payment-note text-xs text-orange-600 mt-1 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {t ? t.checkoutProcessedUSD : "Checkout will be processed in USD"}
          </div>
        </div>
      </div>
    );
  } else {
    // Single currency display
    return (
      <div className={`currency-display single-currency ${className}`}>
        {showLabel && (
          <span className="currency-label text-gray-600 mr-2">
            {labelText}:
          </span>
        )}
        
        <span className={`currency-amount ${sizeClass} text-gray-900`}>
          {formatCurrency(localAmount, localCurrency)}
        </span>
      </div>
    );
  }
};

/**
 * Price Display Component
 * Specialized for displaying product prices with currency conversion
 */
export const PriceDisplay = ({ 
  priceInCents, 
  currencyData, 
  className = '',
  showOriginalPrice = false 
}) => {
  if (!currencyData) {
    return (
      <span className={`price-display ${className}`}>
        ${(priceInCents / 100).toFixed(2)}
      </span>
    );
  }

  const priceInDollars = priceInCents / 100;
  
  if (currencyData.showDualCurrency) {
    const localPrice = (priceInDollars * currencyData.localAmount) / currencyData.usdAmount;
    
    return (
      <div className={`price-display dual-price ${className}`}>
        <div className="local-price text-gray-900">
          {formatCurrency(localPrice, currencyData.localCurrency)}
        </div>
        {showOriginalPrice && (
          <div className="original-price text-sm text-gray-500">
            (${priceInDollars.toFixed(2)} USD)
          </div>
        )}
      </div>
    );
  } else {
    // For non-dual currency, convert the price to local currency
    const localPrice = currencyData.localCurrency === 'USD' ? 
      priceInDollars : 
      (priceInDollars * currencyData.localAmount) / currencyData.usdAmount;
    
    return (
      <span className={`price-display ${className}`}>
        {formatCurrency(localPrice, currencyData.localCurrency)}
      </span>
    );
  }
};

/**
 * Currency Selector Component
 * Allows users to manually select their preferred currency
 */
export const CurrencySelector = ({ 
  currentCurrency, 
  supportedCurrencies, 
  onCurrencyChange,
  className = '' 
}) => {
  const currencyNames = {
    'USD': 'US Dollar',
    'EUR': 'Euro',
    'GBP': 'British Pound',
    'CAD': 'Canadian Dollar',
    'AUD': 'Australian Dollar',
    'JPY': 'Japanese Yen',
    'CHF': 'Swiss Franc',
    'SEK': 'Swedish Krona',
    'NOK': 'Norwegian Krone',
    'DKK': 'Danish Krone',
    'PLN': 'Polish Zloty',
    'CZK': 'Czech Koruna',
    'HUF': 'Hungarian Forint',
    'BRL': 'Brazilian Real',
    'MXN': 'Mexican Peso',
    'SGD': 'Singapore Dollar',
    'HKD': 'Hong Kong Dollar',
    'MYR': 'Malaysian Ringgit',
    'INR': 'Indian Rupee',
    'THB': 'Thai Baht',
    'NZD': 'New Zealand Dollar',
    'ZAR': 'South African Rand',
    'ILS': 'Israeli Shekel',
    'KRW': 'South Korean Won',
    'CNY': 'Chinese Yuan',
    'RUB': 'Russian Ruble',
    'TRY': 'Turkish Lira',
    'IDR': 'Indonesian Rupiah',
    'PHP': 'Philippine Peso',
    'VND': 'Vietnamese Dong',
    'TWD': 'Taiwan Dollar',
    'SAR': 'Saudi Riyal',
    'AED': 'UAE Dirham',
    'EGP': 'Egyptian Pound',
    'NGN': 'Nigerian Naira',
    'GHS': 'Ghanaian Cedi',
    'KES': 'Kenyan Shilling',
    'UGX': 'Ugandan Shilling',
    'TZS': 'Tanzanian Shilling',
    'MAD': 'Moroccan Dirham',
    'TND': 'Tunisian Dinar',
    'DZD': 'Algerian Dinar'
  };

  return (
    <div className={`currency-selector ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Currency
      </label>
      <select
        value={currentCurrency}
        onChange={(e) => onCurrencyChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {supportedCurrencies.map(currency => (
          <option key={currency} value={currency}>
            {getCurrencySymbol(currency)} {currency} - {currencyNames[currency] || currency}
          </option>
        ))}
      </select>
    </div>
  );
};

/**
 * Currency Info Component
 * Shows information about currency conversion and payment method
 */
export const CurrencyInfo = ({ 
  currencyData, 
  paymentMethod, 
  t,
  className = '' 
}) => {
  // Show myPOS currency display
  if (paymentMethod === 'mypos' && currencyData) {
    const currency = currencyData.localCurrency;
    
    // For USD, don't show any notification (no conversion needed)
    if (currency === 'USD') {
      return null;
    }
    
    // For EUR, show direct payment (no conversion)
    if (currency === 'EUR') {
      return (
        <div className={`currency-info bg-blue-50 border border-blue-200 rounded-lg p-3 ${className}`}>
          <div className="text-sm text-blue-800">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Payment Amount:</span>
              <div className="text-right">
                <div className="text-lg font-bold">
                  {formatCurrency(currencyData.localAmount, 'EUR')}
                </div>
                <div className="text-sm text-blue-600">
                  Processed in EUR
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // For other currencies, show conversion to EUR
    const conversionRates = {
      'EUR': 1.0,
      'USD': 0.92,
      'GBP': 1.17,
      'CAD': 0.68,
      'AUD': 0.61,
      'JPY': 0.0061,
      'CHF': 1.05,
      'SEK': 0.085,
      'NOK': 0.085,
      'DKK': 0.13,
      'PLN': 0.22,
      'CZK': 0.039,
      'HUF': 0.0025,
      'BRL': 0.18,
      'MXN': 0.054,
      'SGD': 0.68,
      'HKD': 0.12,
      'MYR': 0.20,
      'INR': 0.011,
      'THB': 0.025,
      'NZD': 0.56,
      'ZAR': 0.051,
      'ILS': 0.25,
      'KRW': 0.00069,
      'CNY': 0.13,
      'RUB': 0.0095,
      'TRY': 0.030,
      'IDR': 0.000059,
      'PHP': 0.016,
      'VND': 0.000037,
      'TWD': 0.029,
      'SAR': 0.25,
      'AED': 0.25,
      'EGP': 0.030,
      'NGN': 0.00069,
      'GHS': 0.069,
      'KES': 0.0069,
      'UGX': 0.00025,
      'TZS': 0.00037,
      'MAD': 0.092,
      'TND': 0.30,
      'DZD': 0.0069
    };
    
    const eurAmount = currencyData.localAmount * (conversionRates[currency] || 0.92);

    return (
      <div className={`currency-info bg-orange-50 border border-orange-200 rounded-lg p-3 ${className}`}>
        <div className="text-sm text-orange-800">
          <div className="flex items-center justify-between">
            <span className="font-semibold">Payment Amount:</span>
            <div className="text-right">
              <div className="text-lg font-bold">
                {formatCurrency(currencyData.localAmount, currencyData.localCurrency)}
              </div>
              <div className="text-sm text-orange-600">
                = {formatCurrency(eurAmount, 'EUR')} EUR
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currencyData || !currencyData.showDualCurrency) {
    return null;
  }

  // Create the translated message with placeholders replaced
  const message = t.cartTotalMessage
    .replace('{currency}', currencyData.localCurrency)
    .replace('{amount}', formatCurrency(currencyData.usdAmount, 'USD'));

  return (
    <div className={`currency-info bg-gray-50 border border-gray-200 rounded-lg p-3 ${className}`}>
      <div className="text-sm text-gray-700">
        <p className="font-medium">
          {message}
        </p>
      </div>
    </div>
  );
};

export default CurrencyDisplay; 